/**
 * KhiKdigoRiskCard — стадий по филтрация и албумин + карта на риска по KDIGO,
 * потвърждение на диагнозата и предупреждение при бързо влошаване.
 * Данни: GET /api/khi/kdigo-risk
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, AlertTriangle, Clock, Info, CheckCircle2, Droplets, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KhiKdigoRiskCard({ cardCls, isDark }) {
  const [d, setD] = useState(null);
  const [acr, setAcr] = useState("");
  const [unit, setUnit] = useState("mg_g");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/khi/kdigo-risk`)
      .then(({ data }) => setD(data))
      .catch(() => setD(null));
  }, []);

  const saveAcr = async () => {
    const value = Number(String(acr).replace(",", "."));
    if (!value || value <= 0) { toast.error("Въведете стойност по-голяма от нула"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/khi/acr`, { value, unit });
      setD(data);
      setAcr("");
      toast.success(`Записано · стадий ${data.a_stage} (${data.saved?.acr_mg_g} mg/g)`);
      try { window.dispatchEvent(new CustomEvent("khi:acr:saved", { detail: data })); } catch { /* noop */ }
    } catch {
      toast.error("Записването не успя");
    } finally {
      setSaving(false);
    }
  };

  if (!d?.heat_map?.length) return null;
  const color = d.risk_bg?.color || "#94A3B8";
  const conf = d.confirmation || {};

  return (
    <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="khi-kdigo-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2">
            <Activity className="w-4 h-4 text-orange-500" />
            Стадий и риск по KDIGO
          </h3>
          <p className="text-[11px] opacity-60 mt-0.5">
            Стадият се определя от филтрацията и от албумина в урината
          </p>
        </div>
        {d.risk && (
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full"
            style={{ background: color + "22", color }} data-testid="khi-kdigo-risk-badge">
            {d.risk_bg?.label_bg}
          </span>
        )}
      </div>

      {/* двата стадия */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className={`rounded-xl p-2.5 ${isDark ? "bg-white/5" : "bg-slate-50"}`}
          data-testid="khi-kdigo-g">
          <div className="text-[9px] opacity-60">филтрация</div>
          <div className="text-lg font-bold">{d.g_stage || "—"}</div>
          <div className="text-[10px] opacity-60">
            {d.egfr != null ? `${d.egfr} mL/min · ` : ""}{d.g_term_bg || ""}
          </div>
        </div>
        <div className={`rounded-xl p-2.5 ${isDark ? "bg-white/5" : "bg-slate-50"}`}
          data-testid="khi-kdigo-a">
          <div className="text-[9px] opacity-60">албумин в урината</div>
          <div className="text-lg font-bold">{d.a_stage || "—"}</div>
          <div className="text-[10px] opacity-60">
            {d.acr_mg_g != null
              ? `${d.acr_mg_g} mg/g (${d.acr_mg_mmol} mg/mmol) · ${d.a_term_bg}`
              : "няма изследване"}
          </div>
        </div>
      </div>

      {d.missing_acr && (
        <div className="text-[11px] mt-2 px-2.5 py-2 rounded-xl bg-sky-500/10 border-l-[3px] border-sky-500"
          data-testid="khi-kdigo-missing-acr">
          <Info className="w-3 h-3 inline mr-1 text-sky-500" />{d.acr_hint_bg}
        </div>
      )}

      {/* въвеждане на албумин в урината */}
      <div className={`mt-3 rounded-xl p-2.5 ${isDark ? "bg-white/5" : "bg-slate-50"}`}
        data-testid="khi-acr-entry">
        <div className="text-[11px] font-bold inline-flex items-center gap-1">
          <Droplets className="w-3 h-3 text-amber-500" />
          {d.acr_mg_g != null ? "Обнови албумина в урината" : "Въведи албумин в урината"}
        </div>
        <div className="text-[10px] opacity-60 mt-0.5">
          Стойността от изследването „албумин/креатинин в урината“. След запис веднага
          виждате стадия A1-A3 и мястото си в картата на риска.
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <input
            type="number" step="0.1" min="0" value={acr}
            onChange={(e) => setAcr(e.target.value)}
            placeholder={unit === "mg_g" ? "напр. 45" : "напр. 5,1"}
            className={`px-2.5 py-1.5 rounded-lg text-[13px] border w-24 ${
              isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
            data-testid="khi-acr-input" />
          <select value={unit} onChange={(e) => setUnit(e.target.value)}
            className={`px-2.5 py-1.5 rounded-lg text-[13px] border ${
              isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
            data-testid="khi-acr-unit">
            <option value="mg_g">mg/g</option>
            <option value="mg_mmol">mg/mmol</option>
          </select>
          <button onClick={saveAcr} disabled={saving || !acr}
            className="text-[12px] font-bold px-3 py-1.5 rounded-lg bg-amber-500 text-white
                       hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="khi-acr-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Запази и виж риска
          </button>
        </div>
        {d.saved_note_bg && (
          <div className="text-[10px] mt-1.5 text-emerald-500" data-testid="khi-acr-saved-note">
            {d.saved_note_bg}
          </div>
        )}
      </div>

      {/* карта на риска */}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[300px] border-separate" style={{ borderSpacing: "2px" }}
          data-testid="khi-kdigo-heatmap">
          <thead>
            <tr className="text-[9px] opacity-60">
              <th className="text-left font-semibold">филтрация</th>
              <th>A1<div className="font-normal">под 30</div></th>
              <th>A2<div className="font-normal">30-299</div></th>
              <th>A3<div className="font-normal">300+</div></th>
            </tr>
          </thead>
          <tbody>
            {d.heat_map.map((row) => (
              <tr key={row.stage}>
                <td className="text-[10px] whitespace-nowrap pr-1">
                  <span className="font-bold">{row.stage}</span>
                  <span className="opacity-55"> {row.range_bg}</span>
                </td>
                {row.cells.map((c) => {
                  const here = row.stage === d.g_stage && c.a_stage === d.a_stage;
                  return (
                    <td key={c.a_stage} className="text-center rounded-md"
                      style={{
                        background: c.color + (here ? "" : "33"),
                        outline: here ? `2px solid ${isDark ? "#fff" : "#0f172a"}` : "none",
                        height: 26,
                      }}
                      data-testid={`khi-kdigo-cell-${row.stage}-${c.a_stage}`}>
                      {here && (
                        <span className="text-[9px] font-bold text-white">Вие</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {d.risk_bg?.share_bg && (
          <div className="text-[10px] opacity-55 mt-1">
            {d.risk_bg.label_bg} — {d.risk_bg.share_bg}
          </div>
        )}
      </div>

      {/* потвърждение на диагнозата */}
      {conf.text_bg && (
        <div className="mt-3 rounded-xl p-2.5"
          style={{
            background: (conf.status === "confirmed" ? "#10B981" : "#F59E0B") + "12",
            borderLeft: `3px solid ${conf.status === "confirmed" ? "#10B981" : "#F59E0B"}`,
          }}
          data-testid="khi-kdigo-confirmation">
          <div className="text-[11px] font-bold inline-flex items-center gap-1"
            style={{ color: conf.status === "confirmed" ? "#10B981" : "#F59E0B" }}>
            {conf.status === "confirmed"
              ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
            {conf.status === "confirmed" ? "Потвърдена диагноза"
              : conf.status === "pending" ? "Изчаква потвърждение" : "Потвърждение"}
          </div>
          <div className="text-[12px] mt-0.5">{conf.text_bg}</div>
          {conf.why_bg && <div className="text-[10px] opacity-60 mt-1">{conf.why_bg}</div>}
        </div>
      )}

      {/* бързо влошаване */}
      {d.rapid_progression && (
        <div className="mt-2 rounded-xl p-2.5 bg-red-500/12 border-l-[3px] border-red-500"
          data-testid="khi-kdigo-rapid">
          <div className="text-[11px] font-bold text-red-500 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Бързо влошаване
          </div>
          <div className="text-[12px] mt-0.5">{d.rapid_progression.text_bg}</div>
        </div>
      )}

      <div className="text-[9px] opacity-50 mt-2">
        {d.conversion_bg} · {d.disclaimer_bg}
      </div>
    </div>
  );
}
